import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Bloco from './componentes/Bloco';
import TituloDaTela from './componentes/TituloDaTela';
import ConsentimentoIA from './componentes/ConsentimentoIA';
import ActionPlan from '../components/ActionPlan';
import { sinaisComTela, estadoDoParecer, contagemDeSinais } from './apresentacao';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Tela Análise do Consultor.
 *
 * A regra que ela materializa: o determinístico aparece SEMPRE. Os sinais, o
 * plano 30-60-90 e a priorização são código — custam zero e são o tier
 * gratuito inteiro. O que varia é o parecer por IA.
 *
 * E quando ele não sai, a tela diz POR QUE e o que fazer. Área vazia é a pior
 * resposta possível: o usuário não sabe se o produto quebrou, se ele fez algo
 * errado, ou se aquilo é pago. O portão de LLM já devolve mensagem e ação
 * legíveis desde a onda 2 — faltava alguém exibir.
 */
const ICONE = {
  critical: <ErrorOutlineIcon fontSize="small" color="error" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />,
  positive: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  info: <InfoOutlinedIcon fontSize="small" color="disabled" />
};

const ROTULO_DA_ACAO = {
  consentir: 'Autorizar o envio para análise por IA',
  ligar_nas_configuracoes: 'Ver as configurações de IA',
  // A ação `assinar` existia no portão desde a onda 2 e não tinha rótulo aqui:
  // a mensagem de bloqueio aparecia sem botão nenhum, e o usuário ficava sabendo
  // que precisava assinar sem ter para onde ir.
  assinar: 'Ver os planos'
};

const TelaAnalise = ({
  leitura,
  onIrPara,
  aoAtualizarConsentimento,
  aoVerPlanos,
  aoPedirParecer,
  gerandoParecer = false,
  aoMarcarTarefa,
  salvandoTarefa = null
}) => {
  const [consentimentoAberto, setConsentimentoAberto] = useState(false);
  const sinais = sinaisComTela(leitura);
  const parecer = estadoDoParecer(leitura);
  const plano = leitura?.insights?.actionPlan || null;
  const textoDoParecer = leitura?.insights?.parecer || null;

  const { criticos, atencao } = contagemDeSinais(leitura);

  /**
   * O botão que refaz o parecer sobre os números de agora.
   *
   * É o MESMO gesto de "Gerar o parecer" — uma chamada de modelo, cobrada, por
   * clique humano — então ele carrega o mesmo estado de trabalho. Sem `disabled`
   * e sem dizer que está trabalhando, três cliques viram três cobranças.
   */
  const botaoDeAtualizar = aoPedirParecer ? (
    <Button
      size="small"
      variant="contained"
      onClick={aoPedirParecer}
      disabled={gerandoParecer}
      startIcon={gerandoParecer ? <CircularProgress size={16} color="inherit" /> : null}
      sx={{ minHeight: 44 }}
    >
      {gerandoParecer ? 'Atualizando o parecer…' : 'Atualizar o parecer'}
    </Button>
  ) : null;

  // Duas das três ações esta tela resolve sozinha, na mesma rota: `aceito: true`
  // liga a flag da empresa e grava o consentimento com a versão do texto que foi
  // exibida. A terceira, `assinar`, agora tem para onde levar — a tela de planos
  // existe, e o convite é ancorado no sinal mais crítico da própria empresa.
  // Botão que não leva a lugar nenhum é pior que nenhum botão: sem quem trate
  // o "assinar", a mensagem sai sem ação em vez de sair com uma ação morta.
  const rotuloDaAcao =
    parecer.acao === 'assinar' && !aoVerPlanos ? null : ROTULO_DA_ACAO[parecer.acao];

  const executarAcao = () => {
    if (parecer.acao === 'assinar') {
      if (aoVerPlanos) aoVerPlanos();
      return;
    }
    if (parecer.acao === 'consentir' || parecer.acao === 'ligar_nas_configuracoes') {
      setConsentimentoAberto(true);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Análise do Consultor"
        descricao="Dado tudo isso, qual é o meu problema principal e o que eu faço primeiro?"
      />
      <Bloco
        titulo="O parecer"
        pergunta="Dado tudo isso, qual é o meu problema principal e o que eu faço primeiro?"
        acao={
          parecer.estado === 'gerado' ? (
            /* O selo diz de cara se o que está abaixo descreve os números de
               agora. Antes ele dizia apenas "gerado por IA", o que faz um
               parecer de três semanas atrás parecer recém-saído. O botão de
               atualizar mora SÓ no aviso, logo abaixo: dois botões com o mesmo
               rótulo na mesma tela é uma escolha que o usuário não tem. */
            <Chip
              size="small"
              color={parecer.desatualizado ? 'warning' : 'primary'}
              variant="outlined"
              label={parecer.desatualizado ? 'Parecer desatualizado' : 'Parecer gerado por IA'}
            />
          ) : (
            // Quem assina e já autorizou o envio ficava sem ação nenhuma: o
            // parecer só nascia numa nova geração de dashboard, por um caminho
            // que ninguém adivinha. Pagar e não ter botão é o pior desenho
            // possível para o único item que a assinatura entrega.
            aoPedirParecer && (parecer.estado === 'naoGerado' || parecer.estado === 'falhou') ? (
              // O botão mais caro do produto. Sem estado de trabalho ele aceitava
              // clique repetido: três cliques em 351 ms geravam TRÊS chamadas de
              // LLM, todas cobradas, para um cliente pagante. `disabled` enquanto
              // trabalha e o próprio rótulo dizendo o que está acontecendo.
              <Button
                size="small"
                variant="contained"
                onClick={aoPedirParecer}
                disabled={gerandoParecer}
                startIcon={gerandoParecer ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ minHeight: 44 }}
              >
                {gerandoParecer
                  ? 'Gerando o parecer…'
                  : parecer.estado === 'falhou' ? 'Tentar de novo' : 'Gerar o parecer'}
              </Button>
            ) : null
          )
        }
      >
        {parecer.mensagem && (
          <Alert
            severity="info"
            action={
              rotuloDaAcao ? (
                <Button color="inherit" size="small" onClick={executarAcao} sx={{ minHeight: 44 }}>
                  {rotuloDaAcao}
                </Button>
              ) : null
            }
            sx={{ mb: 3 }}
          >
            {parecer.mensagem}
          </Alert>
        )}

        {/* O AVISO DE ANALISE ENVELHECIDA.
            Alert, e não Snackbar: ele conta que o texto abaixo descreve número
            que não existe mais, e isso não pode depender de a pessoa estar
            olhando para a tela no segundo em que aparece. Fica até ela resolver
            — atualizando, ou aceitando o parecer como histórico datado. */}
        {parecer.aviso && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>{parecer.aviso.titulo}</AlertTitle>
            {parecer.aviso.corpo}
            {/* O botão fica DENTRO do corpo, e não na coluna de ação do Alert:
                ali ele tinha 90px e o rótulo quebrava em três linhas
                ("Atualizar / o / parecer") no desktop. Forçar uma linha só
                estouraria a largura no celular. */}
            {botaoDeAtualizar && <Box sx={{ mt: 1.5 }}>{botaoDeAtualizar}</Box>}
          </Alert>
        )}

        {textoDoParecer?.gerado && (
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: parecer.desatualizado ? 'warning.main' : 'primary.main',
              bgcolor: 'action.hover'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              O que o consultor lê nos seus números
            </Typography>
            {/* A PROCEDÊNCIA, SEMPRE — não só quando o parecer envelhece.
                Um parecer de consultoria sem data envelhece invisível: o leitor
                não tem como saber se aquilo descreve os números de hoje ou os de
                três semanas atrás. */}
            {parecer.procedencia && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
                {parecer.procedencia}
              </Typography>
            )}
            {textoDoParecer.resumo?.headline && (
              <Typography variant="body1" sx={{ maxWidth: '65ch', mb: 1 }}>
                {textoDoParecer.resumo.headline}
              </Typography>
            )}
            {(textoDoParecer.leitura || []).slice(0, 4).map((item, indice) => (
              <Typography
                key={item.id || `${indice}`}
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: '65ch', mt: 0.75 }}
              >
                {item.message || item.title}
              </Typography>
            ))}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
              O parecer cruza os sinais abaixo. Ele não substitui nenhum deles — os sinais
              continuam sendo calculados aqui, por regra, e continuam completos.
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`${sinais.length} sinais`} />
          {criticos > 0 && <Chip size="small" color="error" variant="outlined" label={`${criticos} críticos`} />}
          {atencao > 0 && <Chip size="small" color="warning" variant="outlined" label={`${atencao} em atenção`} />}
        </Stack>

        {sinais.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum sinal disparou no período analisado.
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {sinais.map((sinal) => (
              <Stack key={sinal.id} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ mt: 0.25 }}>{ICONE[sinal.severidade] || ICONE.info}</Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body1" sx={{ maxWidth: '65ch' }}>
                    {sinal.mensagem}
                  </Typography>
                  {sinal.recomendacao && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: '65ch' }}>
                      {sinal.recomendacao}
                    </Typography>
                  )}
                  {sinal.tela && (
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => onIrPara && onIrPara(sinal.tela)}
                      sx={{ mt: 0.25, ml: -1 }}
                    >
                      Ver de onde vem
                    </Button>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Bloco>

      {/* O plano deixou de ser lista morta: a pessoa marca o que fez, adia o
          que não vai caber e vê o que venceu. Os controles só nascem quando o
          servidor devolveu o plano com identidade — visitante sem conta
          continua vendo o plano inteiro, como leitura. */}
      {plano && (
        <ActionPlan plan={plano} aoMarcar={aoMarcarTarefa} salvando={salvandoTarefa} />
      )}

      <Typography variant="caption" color="text.secondary" sx={{ px: 1, maxWidth: '75ch' }}>
        Sinais e plano de ação são calculados aqui, por regra, sem enviar dado para fora. Eles dizem
        O QUE está acontecendo, e acompanhar as iniciativas não custa assinatura. O parecer por IA
        diz POR QUE e o que atacar primeiro.
      </Typography>

      <ConsentimentoIA
        aberto={consentimentoAberto}
        aoFechar={() => setConsentimentoAberto(false)}
        aoConcluir={aoAtualizarConsentimento}
      />
    </Stack>
  );
};

export default TelaAnalise;
